import { makeAutoObservable, runInAction } from 'mobx';
import { documentRequestTokenApi, documentRequestAuthApi } from 'services/documentRequestApi';
export { DOCUMENT_REQUEST_STATUSES, DOCUMENT_ITEM_TYPES };

const DOCUMENT_REQUEST_STATUSES = {
  REQUESTED: 'REQUESTED',
  PROVIDED: 'PROVIDED',
  COMPLETED: 'COMPLETED',
  CANCELLED: 'CANCELLED'
};

const DOCUMENT_ITEM_TYPES = {
  INVOICE: 'INVOICE',
  AGREEMENT: 'AGREEMENT',
  TRANSPORTATION_DOCUMENTS: 'TRANSPORTATION_DOCUMENTS',
  PROOF_OF_WORK: 'PROOF_OF_WORK',
  BANK_STATEMENT: 'BANK_STATEMENT',
  TAX_DECLARATION: 'TAX_DECLARATION',
  IDENTIFICATION_DOCUMENT: 'IDENTIFICATION_DOCUMENT',
  PROOF_OF_ADDRESS: 'PROOF_OF_ADDRESS',
  PROOF_OF_DIVIDENDS: 'PROOF_OF_DIVIDENDS',
  PURPOSE_OF_PAYMENT: 'PURPOSE_OF_PAYMENT',
  OTHER: 'OTHER'
};

class DocumentRequestStore {
  // Current document request data
  currentRequest = null;
  items = [];
  status = null;
  expiresAt = null;

  // Token for no-auth access
  token = null;
  requestId = null;

  // UI state
  isLoading = false;
  error = null;
  uploadProgress = {};
  currentStep = 0;

  // List of requests for authorized users
  requestsList = [];

  constructor() {
    makeAutoObservable(this);
  }

  // Set loading state
  setIsLoading(status) {
    this.isLoading = status;
    this.error = null;
  }

  // Set error
  setError(error) {
    this.error = error;
    this.isLoading = false;
  }

  // Initialize from URL params for no-auth access
  initializeFromUrl(id, token) {
    this.requestId = id;
    this.token = token;
  }

  // Set current step in multi-step form
  setCurrentStep(step) {
    this.currentStep = step;
  }

  // Fetch document request using token (no-auth)
  async fetchRequestByToken(id, token) {
    try {
      this.setIsLoading(true);

      // Call real API
      const response = await documentRequestTokenApi.getRequest(id, token);

      runInAction(() => {
        this.currentRequest = response;
        this.items = response.items || [];
        this.status = response.status;
        this.expiresAt = response.expiresAt;
        this.isLoading = false;
      });

      return response;
    } catch (error) {
      this.setError(error);
      throw error;
    }
  }

  // Fetch document request for authorized user
  async fetchRequestById(accountNumber, requestId) {
    try {
      this.setIsLoading(true);

      const response = await documentRequestAuthApi.getRequest(accountNumber, requestId);

      runInAction(() => {
        this.currentRequest = response;
        this.items = response.items || [];
        this.status = response.status;
        this.expiresAt = response.expiresAt;
        this.requestId = requestId; // Make sure requestId is set
        this.isLoading = false;
      });

      return response;
    } catch (error) {
      this.setError(error);
      throw error;
    }
  }

  // Fetch all document requests for authorized user
  async fetchRequestsList(customerNumber) {
    try {
      this.setIsLoading(true);

      // First get the list of all document requests
      const requestsList = await documentRequestAuthApi.getRequestsList(customerNumber);

      // If we have requests, fetch detailed info for each one
      let detailedRequests = [];
      if (requestsList && requestsList.length > 0) {
        // Fetch detailed info for each request in parallel
        const detailPromises = requestsList.map((request) =>
          documentRequestAuthApi.getRequest(customerNumber, request.id).catch((err) => {
            // Return basic request data if detail fetch fails
            return request;
          })
        );

        detailedRequests = await Promise.all(detailPromises);
      }

      runInAction(() => {
        this.requestsList = detailedRequests;
        this.isLoading = false;
      });

      return detailedRequests;
    } catch (error) {
      this.setError(error);
      throw error;
    }
  }

  // Submit item with files (no-auth)
  async submitItemByToken(itemType, files, comment, deletedDocumentIds = []) {
    try {
      this.setIsLoading(true);

      // Call real API
      const response = await documentRequestTokenApi.submitItem(
        this.requestId,
        this.token,
        itemType,
        files,
        comment,
        deletedDocumentIds
      );

      runInAction(() => {
        // Update the item in the items array
        const itemIndex = this.items.findIndex((item) => item.type === itemType);
        if (itemIndex !== -1) {
          this.items[itemIndex] = response;
        }
        this.isLoading = false;
        this.error = null; // Clear any previous errors on success
      });

      return response;
    } catch (error) {
      this.setError(error);
      throw error;
    }
  }

  // Submit item with files (authorized)
  async submitItem(accountNumber, itemType, files, comment, deletedDocumentIds = []) {
    try {
      this.setIsLoading(true);

      const response = await documentRequestAuthApi.submitItem(
        accountNumber,
        this.requestId,
        itemType,
        files,
        comment,
        deletedDocumentIds
      );

      runInAction(() => {
        // Update the item in the items array
        const itemIndex = this.items.findIndex((item) => item.type === itemType);
        if (itemIndex !== -1) {
          this.items[itemIndex] = response;
        }
        this.isLoading = false;
        this.error = null; // Clear any previous errors on success
      });

      return response;
    } catch (error) {
      this.setError(error);
      throw error;
    }
  }

  // Submit the entire request (no-auth)
  async submitRequestByToken() {
    try {
      this.setIsLoading(true);

      // Call real API
      const response = await documentRequestTokenApi.submitRequest(this.requestId, this.token);

      runInAction(() => {
        this.status = response.status;
        this.isLoading = false;
      });

      return response;
    } catch (error) {
      this.setError(error);
      throw error;
    }
  }

  // Submit the entire request (authorized)
  async submitRequest(accountNumber) {
    try {
      this.setIsLoading(true);

      const response = await documentRequestAuthApi.submitRequest(accountNumber, this.requestId);

      runInAction(() => {
        this.status = response.status;
        this.isLoading = false;
      });

      return response;
    } catch (error) {
      this.setError(error);
      throw error;
    }
  }

  // Download document file (no-auth)
  async downloadDocumentByToken(documentId) {
    try {
      const response = await documentRequestTokenApi.downloadDocument(this.requestId, documentId, this.token);

      return response;
    } catch (error) {
      this.setError(error);
      throw error;
    }
  }

  // Download document file (authorized)
  async downloadDocument(accountNumber, documentId) {
    try {
      const response = await documentRequestAuthApi.downloadDocument(accountNumber, this.requestId, documentId);

      return response;
    } catch (error) {
      this.setError(error);
      throw error;
    }
  }

  // Check if all items have been submitted
  get allItemsSubmitted() {
    return this.items.every((item) => item.submittedAt !== null);
  }

  // Check if request can be submitted
  get canSubmitRequest() {
    return this.status === DOCUMENT_REQUEST_STATUSES.REQUESTED && this.allItemsSubmitted;
  }

  // Check if request is expired
  get isExpired() {
    if (!this.expiresAt) return false;
    return new Date(this.expiresAt) < new Date();
  }

  // Clear store
  clear() {
    this.currentRequest = null;
    this.items = [];
    this.status = null;
    this.expiresAt = null;
    this.token = null;
    this.requestId = null;
    this.isLoading = false;
    this.error = null;
    this.uploadProgress = {};
    this.currentStep = 0;
    this.requestsList = [];
  }
}

export default new DocumentRequestStore();
